import { useEffect, useState } from "react";
import { COLOR, FONT } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import CookActivityCard from "./CookActivityCard";

// FamilyKitchenView — read-only "family kitchen status board" view.
// One CookActivityCard per active cook visible to this user.
// Vertical-stacked. Tap any card → that cook's CookMode opens.
//
// Two surfaces consume this:
//   • Home tab top — when activeCooks.length > 0, render compact above
//     the snapshot panel.
//   • Home → Cooks sub-tab — replaces the previous conductor.
//
// No claim buttons, no helper-pull, no plan-a-meal CTA, no debug
// strip. Those got descoped after the user said the conductor view
// "sucks." The minimum viable conductor is just the cards; further
// affordances will be added once direction is confirmed.
//
// Props:
//   cooks         — array from useActiveCookSessions
//   currentUserId — own user id (for "you" labels)
//   avatarFor     — fn(userId) → profile-pic URL or null. Comes from
//                   App.jsx's relationships closure; covers self +
//                   family + friends instantly without a network read.
//   onJumpToCook  — fn(recipe, opts) → opens CookMode for that cook

export default function FamilyKitchenView({ cooks, currentUserId, avatarFor, onJumpToCook }) {
  // profileInfo: userId → { name, avatarUrl }. Backstops avatarFor
  // for cook owners that aren't in the viewer's relationships
  // closure (e.g. team-cook participants from outside the family).
  const [profileInfo, setProfileInfo] = useState(() => new Map());

  useEffect(() => {
    if (!Array.isArray(cooks) || cooks.length === 0) return;
    const ids = new Set();
    for (const c of cooks) {
      if (c?.session?.user_id && c.session.user_id !== currentUserId) {
        ids.add(c.session.user_id);
      }
    }
    if (ids.size === 0) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("id, name, avatar_url")
          .in("id", Array.from(ids));
        if (!alive || !Array.isArray(data)) return;
        const map = new Map();
        for (const p of data) {
          const first = (p?.name || "").split(/\s+/)[0] || "";
          map.set(p.id, { name: first, avatarUrl: p?.avatar_url || null });
        }
        setProfileInfo(map);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [cooks, currentUserId]);

  if (!Array.isArray(cooks) || cooks.length === 0) {
    return (
      <div style={{
        fontFamily: FONT.detail,
        fontStyle: "italic",
        fontSize: 14,
        color: COLOR.muted,
        textAlign: "center",
        padding: "32px 20px",
      }}>
        No active cooks right now.
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {cooks.map((cook) => {
        const ownerId = cook?.session?.user_id;
        const fromProfiles = ownerId ? profileInfo.get(ownerId) : null;
        const ownerName = ownerId && ownerId !== currentUserId
          ? fromProfiles?.name || null
          : null;
        // avatarFor wins (instant, cached); profileInfo backstops
        // for cook owners outside the relationships closure.
        const ownerAvatarUrl = ownerId
          ? (avatarFor?.(ownerId) || fromProfiles?.avatarUrl || null)
          : null;
        return (
          <CookActivityCard
            key={cook.session.id}
            cook={cook}
            onTap={onJumpToCook}
            ownerName={ownerName}
            ownerAvatarUrl={ownerAvatarUrl}
          />
        );
      })}
    </div>
  );
}
